import { useEffect, useState } from "react";
import { Dialog } from "../../../components/ui/dialog";
import CommissionCard from "./CommissionCard";
import NewSimulationCard from "./NewSimulationCard";
import ChooseSimulationModal from "./ChooseSimulationModal";
import FgtsModal from "./Simulations/Fgts";
import CrefazModal from "./Simulations/Crefaz";
import { PartnerType } from "@/app/utils/partners.enum";
import SalvcarModal from "./Simulations/Salvcar";
import PanModal from "./Simulations/Pan";
import CltModal from "./Simulations/Clt";
import CreateLinkModal from "../../Links/components/CreateLinkModal";
import { selfHireService } from "@/app/services/selfHireService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/view/components/ui/use-toast";
import QRCodeModal from "../../Links/components/QRCodeModal";
import { externalConfigService } from "@/app/services/externalConfigService";
import AlertModal from "./AlertModal";
import CrefisaModal from "./Simulations/Crefisa";

type StepType = PartnerType | "chooseSimulation" | null;

export default function SellerCards({ value = 0 }: { value?: number }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<StepType>(null);
  const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
  const [newLinkName, setNewLinkName] = useState("");
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<{ name: string }>({
    name: "",
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleOpen = () => {
    setOpen(true);
    setStep("chooseSimulation");
  };

  const handleOptionSelect = (option: PartnerType) => setStep(option);

  const handleBackToChoose = () => setStep("chooseSimulation");

  const { mutate: createLinkMutation, isPending } = useMutation({
    mutationFn: async ({
      name,
      tables,
    }: {
      name: string;
      tables: string[];
    }) => {
      return await selfHireService.createLink({ name, tables });
    },
    onSuccess: (variables) => {
      queryClient.invalidateQueries({ queryKey: ["AllUserLinks"] });

      setIsCreateLinkModalOpen(false);

      setNewLinkName("");
      setSelectedTables([]);

      setSelectedLink({ name: variables.name });
      setIsModalOpen(true);

      toast({
        variant: "default",
        title: "Link criado com sucesso!",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title:
          error?.message || "Não foi possível criar o link, tente novamente",
      });
    },
  });

  const handleCreateLink = () => {
    if (newLinkName && selectedTables) {
      createLinkMutation({
        name: newLinkName.trim(),
        tables: selectedTables,
      });
    }
  };

  const handleOpenLinkModal = () => {
    setOpen(false);
    setIsCreateLinkModalOpen(true);
  };

  const { data: externalProducts } = useQuery({
    queryKey: ["externalProducts"],
    queryFn: () => externalConfigService.getProducts(),
  });

  const shouldShowAlertCondition =
    step != null &&
    step !== "chooseSimulation" &&
    !!externalProducts?.[step as PartnerType]?.alert?.active;

  const currentAlert =
    step != null ? externalProducts?.[step as PartnerType]?.alert : undefined;

  useEffect(() => {
    if (shouldShowAlertCondition) setIsAlertOpen(true);
  }, [shouldShowAlertCondition]);

  return (
    <>
      <CommissionCard value={value} />

      <Dialog open={open} onOpenChange={setOpen}>
        <NewSimulationCard onClick={handleOpen} />

        {step === "chooseSimulation" && (
          <ChooseSimulationModal
            handleOpenLinkModal={handleOpenLinkModal}
            onOptionSelect={handleOptionSelect}
            externalProducts={externalProducts}
          />
        )}

        {step === PartnerType.FGTS && <FgtsModal onBack={handleBackToChoose} />}

        {step === PartnerType.CREFAZ && (
          <CrefazModal
            onBack={handleBackToChoose}
            onClose={() => setOpen(false)}
          />
        )}

        {step === PartnerType.SALVCAR && (
          <SalvcarModal onBack={handleBackToChoose} />
        )}

        {step === PartnerType.PAN && <PanModal onBack={handleBackToChoose} />}

        {step === PartnerType.CLT && <CltModal onBack={handleBackToChoose} />}

        {step === PartnerType.CREFISA && (
          <CrefisaModal
            onBack={handleBackToChoose}
            onClose={() => setOpen(false)}
          />
        )}
      </Dialog>

      <AlertModal
        isOpen={isAlertOpen}
        onOpenChange={(nextOpen) => {
          setIsAlertOpen(nextOpen);
          if (!nextOpen && currentAlert?.block) setStep("chooseSimulation");
        }}
        alert={currentAlert}
      />

      <CreateLinkModal
        visible={isCreateLinkModalOpen}
        newLinkName={newLinkName}
        setNewLinkName={setNewLinkName}
        selectedTables={selectedTables}
        setSelectedTables={setSelectedTables}
        onCreateLink={handleCreateLink}
        isLoading={isPending}
        onClose={() => setIsCreateLinkModalOpen(false)}
      />

      <QRCodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        linkName={selectedLink.name}
      />
    </>
  );
}
